import { Controller } from '@hotwired/stimulus'
import bootstrap from 'bootstrap/dist/js/bootstrap'
import markVisibleEdges from '../utils/visible_edges'

const SAVED_MESSAGE = 'The menu changes have been saved.'
const LOAD_FAILED_MESSAGE = 'The dish list could not be loaded. Close this and try again.'
const SAVE_FAILED_MESSAGE = 'The changes could not be saved. Please try again.'

export default class extends Controller {
  static targets = [
    'groupForm', 'usageBody', 'usageSpinner', 'usageError', 'usageSubmit', 'groupLink', 'groupRow',
    'usageBadge', 'usageButton', 'successToast', 'successToastBody', 'errorToast', 'errorToastBody',
    'searchInput', 'searchEmpty', 'searchEmptyQuery', 'listFrame', 'listScroll', 'countBadge',
    'stickyCol', 'formCol'
  ]

  connect() {
    this.initTooltips()
    this.sizeColumn()
    this.scrollSelectedIntoView()
    this.updateScrollFade()
  }

  disconnect() {
    this.tooltips?.forEach((tip) => tip.dispose())
    this.tooltips = null
  }

  // data-tooltip, not data-bs-toggle="tooltip": these buttons already spend that attribute on
  // "collapse". hover-only trigger (no focus) — the default keeps the tip pinned open after a
  // click leaves the button focused — and an explicit hide on click, because collapsing hides
  // the button itself and a display:none trigger never gets the mouseleave.
  initTooltips() {
    this.tooltips = Array.from(this.element.querySelectorAll('[data-tooltip]')).map((el) => {
      const tip = bootstrap.Tooltip.getOrCreateInstance(el, { trigger: 'hover' })
      el.addEventListener('click', () => tip.hide())
      return tip
    })
  }

  // The stylesheet's viewport-sized cap only fits once the column has actually stuck; on load it
  // sits below the navbar and the settings pills, so a static 100vh cap runs off the screen. The
  // cap is re-derived from the column's live offset here — on load, window scroll and resize
  // (wired on the root element) — shrinking to what is really visible and growing back to the
  // full cap as the page scrolls the chrome away.
  // rAF-coalesced: the handler is wired to raw window scroll/resize, and its body is a
  // read-write-read layout cycle that would otherwise run on every scroll tick.
  sizeColumn() {
    if (this.sizeQueued) return

    this.sizeQueued = true
    requestAnimationFrame(() => {
      this.sizeQueued = false
      this.applyColumnSize()
    })
  }

  applyColumnSize() {
    if (!this.hasStickyColTarget) return

    const cols = [this.stickyColTarget]
    if (this.hasFormColTarget) cols.push(this.formColTarget)

    if (window.innerWidth < 992) {
      // Stacked layout: nothing is capped — the list scroll area has its own 45vh limit.
      cols.forEach((col) => { col.style.maxHeight = '' })
      this.updateScrollFade()
      return
    }

    const bottomGap = 16
    // Floored at 0: when the page does scroll, the columns' top goes negative — fed in raw, that
    // inflates the cap past the viewport and releases the clamp entirely.
    const top = Math.max(0, this.stickyColTarget.getBoundingClientRect().top)
    const available = window.innerHeight - top - bottomGap
    // Both columns get the same cap so the whole builder fits the viewport and each side scrolls
    // internally — the page itself shouldn't need to scroll.
    cols.forEach((col) => { col.style.maxHeight = `${Math.max(200, available)}px` })
    this.updateScrollFade()
  }

  rowPointerDown(event) {
    this.rowPointerStart = { x: event.clientX, y: event.clientY }
  }

  // The whole list row selects the group, not just its name link. Skipped when the click landed
  // on a real link (it navigates itself) or when the pointer travelled — Sortable can end a short
  // drag as a click, and reordering shouldn't also switch the form.
  selectRow(event) {
    if (event.target.closest('a')) return

    const start = this.rowPointerStart
    if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > 5) return

    event.currentTarget.querySelector('[data-modifier-builder-target~="groupLink"]')?.click()
  }

  // The picker is fetched every time the modal opens. Until it lands, the body holds the previous
  // group's dishes and Save has no form to submit, so both are reset here rather than left as-is.
  usageLoading(event) {
    event.stopPropagation()
    this.usageSubmitTarget.disabled = true
    this.usageBodyTarget.innerHTML = this.usageSpinnerTarget.innerHTML
  }

  usage(event) {
    event.stopPropagation()
    const [_data, _status, xhr] = event.detail
    this.replaceUsageBody(xhr.response)
  }

  // Both the body and a toast: the modal is already open on a spinner that would otherwise turn
  // forever, and the toast is what the restaurant sees if it has scrolled away from it.
  usageLoadFailed(event) {
    event.stopPropagation()
    this.usageBodyTarget.innerHTML = this.usageErrorTarget.innerHTML
    this.showError(LOAD_FAILED_MESSAGE)
  }

  // Save is disabled for the round trip: the request rewrites the whole attachment set, so a second
  // click landing before the first response would re-send state the server has already replaced.
  usageSubmitting() {
    this.usageSubmitTarget.disabled = true
  }

  // Bulk assignment saved: swap in the refreshed picker, then refresh the usage counts outside the
  // modal from the <template> fragments the server rendered alongside it.
  usageSaved(event) {
    const [_data, _status, xhr] = event.detail
    this.replaceUsageBody(xhr.response)

    const root = this.usageBodyTarget.firstElementChild
    if (root) {
      const swap = (targets, template) => {
        if (!template) return
        targets
          .filter((el) => el.dataset.groupId === root.dataset.groupId)
          .forEach((el) => { el.innerHTML = template.innerHTML })
      }

      swap(this.usageBadgeTargets, root.querySelector('[data-usage-badge-html]'))
      swap(this.usageButtonTargets, root.querySelector('[data-usage-button-html]'))

      // The save changed which dishes the group is attached to — keep the filter haystack honest.
      this.groupRowTargets
        .filter((row) => row.dataset.groupId === root.dataset.groupId)
        .forEach((row) => { row.dataset.itemNames = root.dataset.itemNames || '' })
    }

    this.showSuccess(SAVED_MESSAGE)
  }

  // Without this a rejected or dropped save is completely silent: the ticks stay on screen and read
  // as saved.
  usageFailed() {
    this.usageSubmitTarget.disabled = false
    this.showError(SAVE_FAILED_MESSAGE)
  }

  // The + icon fetched the create form; no group is being edited while it is up, so the list
  // highlight clears too.
  newForm(event) {
    const [_data, _status, xhr] = event.detail
    this.groupFormTarget.innerHTML = xhr.response
    this.highlight(null)
  }

  newFormSubmit(event) {
    const [_data, _status, xhr] = event.detail
    this.groupFormTarget.innerHTML = xhr.response

    const failure = this.groupFormTarget.querySelector('[data-form-error]')
    if (failure) {
      this.showError(failure.textContent.trim() || SAVE_FAILED_MESSAGE)
      return
    }

    this.appendGroupRow()
    this.showSuccess(SAVED_MESSAGE)
  }

  // A group created inline has no list row yet. The create response ships one as a template
  // (see _edit); it goes to the bottom, where acts_as_list put the group, and Sortable picks the
  // new <li> up on its own — it reads the list's children at drag time.
  appendGroupRow() {
    const template = this.groupFormTarget.querySelector('[data-new-list-item-html]')
    const list = this.element.querySelector('.modifier-list')
    if (!template || !list) return

    const item = document.createElement('li')
    item.className = 'list-group-item'
    item.dataset.sortableUpdateUrl = template.dataset.updateUrl
    item.innerHTML = template.innerHTML
    list.appendChild(item)

    // Derived, not incremented: parsing the displayed text drifts as soon as any path removes a
    // row without also decrementing.
    if (this.hasCountBadgeTarget) {
      this.countBadgeTarget.textContent = this.groupRowTargets.length
    }

    this.highlight(template.dataset.groupId)
    // Re-filtering (not just re-marking edges) keeps an active query honest about the new row.
    if (this.searchInputTarget.value.trim() !== '') {
      this.filterGroups()
    } else {
      this.markEdges()
      this.updateScrollFade()
    }
  }

  groupForm(event) {
    const [_data, _status, xhr] = event.detail
    this.groupFormTarget.innerHTML = xhr.response
    this.highlight(event.currentTarget.dataset.groupId)
  }

  // The list is server-rendered once, so the server's initial mark would stick for the whole
  // session. Colours live on .modifier-group-link in SCSS so the two sides can't drift.
  highlight(groupId) {
    this.groupLinkTargets.forEach((link) => {
      link.classList.toggle('is-selected', link.dataset.groupId === groupId)
    })
    this.scrollSelectedIntoView()
  }

  filterGroups() {
    const query = this.searchInputTarget.value.trim().toLowerCase()

    this.groupRowTargets.forEach((row) => {
      // Two haystacks: the group's own name plus the dishes it's attached to, so a meal name
      // surfaces its modifiers.
      const haystack = `${row.dataset.name} ${row.dataset.itemNames || ''}`
      row.closest('.list-group-item').hidden = query !== '' && !haystack.includes(query)
    })

    const anyVisible = this.groupRowTargets.some((row) => !row.closest('.list-group-item').hidden)
    this.searchEmptyTarget.hidden = query === '' || anyVisible
    this.searchEmptyQueryTarget.textContent = this.searchInputTarget.value.trim()

    this.markEdges()

    // SortableJS reports drop positions as raw DOM indexes, counting hidden rows too, so a reorder
    // while filtered would save the wrong position. The event flips Sortable's own disabled option
    // (pointer-events can't do it any more — the whole row is the handle and that would kill the
    // links); the class only carries the dimmed-grip styling.
    const list = this.groupRowTargets[0]?.closest('.modifier-list')
    if (list) {
      const disabled = query !== ''
      list.classList.toggle('modifier-list--drag-disabled', disabled)
      list.dispatchEvent(new CustomEvent('sortable:disabled', { detail: { disabled } }))
    }

    this.updateScrollFade()
  }

  focusSearch() {
    this.searchInputTarget.focus()
  }

  // Collapsing the field also drops its query — a hidden filter would silently keep rows hidden.
  clearSearch() {
    if (this.searchInputTarget.value === '') return

    this.searchInputTarget.value = ''
    this.filterGroups()
  }

  // The scrollbar is hidden, so this fade is the only sign there are more groups below. Off at
  // the bottom, so the list does not read as endless.
  updateScrollFade() {
    const el = this.listScrollTarget
    const more = el.scrollHeight - el.clientHeight - el.scrollTop > 4
    this.listFrameTarget.classList.toggle('has-more', more)
  }

  markEdges() {
    markVisibleEdges(this.groupRowTargets.map((row) => row.closest('.list-group-item')))
  }

  // The list scrolls inside a capped panel, so the selected group can sit out of sight both on
  // load (deep link) and after a click near the container's edge.
  scrollSelectedIntoView() {
    const selected = this.groupLinkTargets.find((link) => link.classList.contains('is-selected'))
    if (!selected) return

    selected.closest('.list-group-item')?.scrollIntoView({ block: 'nearest' })
  }

  modifierFormSubmit(event) {
    const [_data, _status, xhr] = event.detail
    this.groupFormTarget.innerHTML = xhr.response

    // The server puts validation failures in flash.now and the form partial ships the message as a
    // hidden payload — there is no layout on this response to render the flash itself.
    const failure = this.groupFormTarget.querySelector('[data-form-error]')
    if (failure) {
      this.showError(failure.textContent.trim() || SAVE_FAILED_MESSAGE)
      return
    }

    this.refreshGroupRow()
    this.showSuccess(SAVED_MESSAGE)
  }

  // A rename or a changed minimum limit changes the list row too, and the row is rendered outside
  // the form that was just replaced. The server sends a fresh copy of it with the response.
  refreshGroupRow() {
    const template = this.groupFormTarget.querySelector('[data-group-row-html]')
    if (!template) return

    this.groupRowTargets
      .filter((row) => row.dataset.groupId === template.dataset.groupId)
      .forEach((row) => {
        row.innerHTML = template.innerHTML
        // A rename also changes what the search should match against.
        row.dataset.name = template.dataset.name
      })
  }

  // modifier-usage sits on the modal, above the body being replaced, so it cannot see the swap on
  // its own — the event tells it to drop a filter that no longer matches what is on screen.
  replaceUsageBody(html) {
    this.usageBodyTarget.innerHTML = html
    this.usageSubmitTarget.disabled = false
    this.usageBodyTarget.dispatchEvent(new CustomEvent('usage:loaded', { bubbles: true }))
  }

  showSuccess(message) {
    this.showToast(this.successToastTarget, this.successToastBodyTarget, message)
  }

  showError(message) {
    this.showToast(this.errorToastTarget, this.errorToastBodyTarget, message)
  }

  // Targets, not a document lookup: #liveToast is not unique on a page that also carries a flash,
  // and the layout's copy comes first in document order.
  showToast(toast, body, message) {
    body.textContent = message
    bootstrap.Toast.getOrCreateInstance(toast).show()
  }
}
